import _ from "lodash";

import { Group } from "@dashboard/devices/types";
import { Table } from "@tanstack/react-table";

import { GroupWithChildren } from "./GroupFilterHeader";

export const buildGroupTree = (groups: Group[], parentId: number | null = null): GroupWithChildren[] => {
    return groups
        .filter((group) => group.parentId === parentId)
        .map((group) => ({
            ...group,
            children: buildGroupTree(groups, group.id),
        }));
};
export const getAllGroupIds = (groups: GroupWithChildren[]): number[] => {
    return groups.reduce((acc: number[], group) => {
        return [...acc, group.id, ...getAllGroupIds(group.children)];
    }, []);
};

type GroupSubtreeSelection = "none" | "partial" | "full";

export const getSubtreeSelectionState = (
    group: GroupWithChildren,
    values: Record<string, boolean | undefined> | undefined,
): GroupSubtreeSelection => {
    const keys = getAllGroupIds([group]).map((id) => `$${id}`);
    const selectedCount = keys.filter((k) => values?.[k]).length;
    if (selectedCount === 0) {
        return "none";
    }
    if (selectedCount === keys.length) {
        return "full";
    }
    return "partial";
};

export function setSubtreeCheckboxValues(
    group: GroupWithChildren,
    checked: boolean,
    onChange: (name: string, value: boolean) => void,
): void {
    getAllGroupIds([group]).forEach((id) => {
        onChange(`$${id}`, checked);
    });
}

export const getAncestorGroupIds = (groups: Group[], targetId: number): number[] => {
    const byId = new Map(groups.map((group) => [group.id, group]));
    const ancestors: number[] = [];
    let parentId: number | null = byId.get(targetId)?.parentId ?? null;

    while (parentId !== null) {
        const parent = byId.get(parentId);
        if (!parent) {
            break;
        }
        ancestors.push(parent.id);
        parentId = parent.parentId ?? null;
    }

    return ancestors;
};

export const applyHierarchyCheckboxToggle = (
    flatGroups: Group[],
    targetGroup: GroupWithChildren,
    values: Record<string, boolean | undefined> | undefined,
    onChange: (name: string, value: boolean) => void,
): void => {
    const targetKey = `$${targetGroup.id}`;
    const isTargetInFilter = Boolean(values?.[targetKey]);
    const descendantIds = getAllGroupIds(targetGroup.children);

    const parentUncheckedAllDescendantsChecked =
        !isTargetInFilter &&
        descendantIds.length > 0 &&
        descendantIds.every((id) => values?.[`$${id}`]);

    if (parentUncheckedAllDescendantsChecked) {
        setSubtreeCheckboxValues(targetGroup, false, onChange);
        return;
    }

    if (isTargetInFilter) {
        onChange(targetKey, false);
        getAncestorGroupIds(flatGroups, targetGroup.id).forEach((id) => {
            onChange(`$${id}`, false);
        });
    } else {
        onChange(targetKey, true);
        descendantIds.forEach((id) => {
            onChange(`$${id}`, true);
        });
    }
};

const getColumnFilters = <T,>(table: Table<T>, headerId: string) => {
    const columnFilters = table.getState().columnFilters.find((item) => item.id === headerId)?.value;

    if (Array.isArray(columnFilters)) {
        return columnFilters;
    }
    return [];
};
export const getInitialFilters = <T,>(table: Table<T>, groups: Group[], headerId: string) => {
    const columnFilters = getColumnFilters(table, headerId);
    const allGroups = groups.reduce((acc, group) => ({ ...acc, [`$${group.id}`]: false }), {});
    const filterGroups = columnFilters.reduce((acc, item) => ({ ...acc, [`$${item}`]: true }), {});

    return _.merge({}, allGroups, filterGroups);
};
